import React, { memo, FC, useMemo } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  TableCellLayout,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  tokens,
} from '@fluentui/react-components';

import { StatusIcon } from './status-icon';
import { SearchTermActions } from './search-term-actions';

export const useStyles = makeStyles({
  entityNameLink: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    ...shorthands.overflow('hidden'),
    width: '100%',
  },
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
  },
  entityNameRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
  },
  searchTermActionRow: {
    // cannot find better way, use this hack to align the button with the text
    display: 'inline-block',
    '& .search-term-added + .search-term-exclude': {
      left: '0',
    },
  },
  searchTermButton: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: 'normal',
    ...shorthands.padding('0px'),
    position: 'relative',
    // cannot find better way, use this hack to align the button with the text
    left: '-12px',
    top: '-2px',
  },
  leftSide: {
    ...shorthands.margin('0px', '8px', '0px', '0px'),
  },
});

export interface IEntityNameProps {
  entityType: string;
  href?: string;
  name: string;
  status?: string;
  isExperiment: boolean;
  popoverContent?: React.ReactNode;
  triggerSearchTermDrawer?: Function;
  className?: string;
  searchTermStatus?: string;
  i18n?: any;
  rowRawItem?: any;
}

export const EntityName: FC<IEntityNameProps> = memo(({
  entityType,
  href,
  name,
  status,
  isExperiment,
  popoverContent,
  triggerSearchTermDrawer,
  className,
  i18n,
  searchTermStatus,
  rowRawItem,
}: IEntityNameProps) => {
  const classes = useStyles();

  const content = useMemo(() => (
    <div className={classNames(className, classes.container)}>
      <div className={classNames('default-entity-name-row', classes.entityNameRow)}>
        <StatusIcon status={status} isExperiment={isExperiment} entityType={entityType} />
        {
          href ?
            <Link className={classNames('entity-name-text', classes.entityNameLink)} to={href}>{name}</Link>
            : <div className={classNames('entity-name-text', classes.entityNameLink)}>{name}</div>
        }
      </div>
      {
        entityType === 'SearchTerm' &&
        <SearchTermActions
          triggerSearchTermDrawer={triggerSearchTermDrawer}
          i18n={i18n}
          searchTermStatus={searchTermStatus}
          rowRawItem={rowRawItem}
        />
      }
    </div>
  ), [className, classes, status, isExperiment, entityType, href, name,
    triggerSearchTermDrawer, i18n, searchTermStatus, rowRawItem]);

  if (popoverContent) {
    return (
      <TableCellLayout truncate>
        <Popover openOnHover mouseLeaveDelay={0}>
          <PopoverTrigger>
            {content}
          </PopoverTrigger>
          <PopoverSurface tabIndex={-1}>
            {popoverContent}
          </PopoverSurface>
        </Popover>
      </TableCellLayout>
    );
  }

  return (
    <TableCellLayout truncate>
      <Tooltip content={name} relationship="label">
        {content}
      </Tooltip>
    </TableCellLayout>
  );
});

EntityName.defaultProps = {
  popoverContent: null,
  href: null,
  className: 'entity-name',
  i18n: null,
  searchTermStatus: null,
  status: null,
  triggerSearchTermDrawer: undefined,
  rowRawItem: null,
};
